import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';

import { Modal } from 'shared/ui/modal';
import { MODAL_ANIMATION_DURATION } from 'shared/config/constants';
import {
  enableScroll,
  getAssignmentContent,
  useAnimatedMounting,
  useEscapeKey,
} from 'shared/lib/utils';
import { useAppDispatch, useAppSelector } from 'shared/lib/redux';
import { dateService } from 'shared/lib/date';
import {
  Assignment,
  convertAssignmentIntoAssignmentPayload,
  useEditAssignmentMutation,
} from 'shared/api/assignment-service';
import {
  eventService,
  generateAssignmentActionsContextEventPayload,
  generateUpdateAssignmentResultEventsPayload,
  getPage,
  XApiEventKeys,
} from 'shared/api/event-service';
import { ASSIGNMENT_UPDATED_EVENT, RealtimeService } from 'shared/lib/realtime';
import {
  getAssignmentSection,
  isScheduledAssignment,
} from 'entities/assignments';
import { useClassData } from 'entities/classes';
// eslint-disable-next-line boundaries/element-types
import { RESPONSIVE_BIG_MOBILE, useScreenWidth } from 'features/responsive';

import {
  setEditingAssignment,
  setIsAssignmentActionNotificationActive,
  setIsAssignmentPanelOpened,
  setAssignmentActionErrorMessage,
} from '../model';
import {
  getMaxDate,
  getMinDate,
  setCalendarButtonsStatus,
  setDateInputPosition,
} from '../lib/utils';
import {
  useAssignmentActionTimestampHandlers,
  useCalendarsKeyboardClose,
} from '../lib/hooks';
import {
  CalendarWidgetPositions,
  getAssignmentsErrorMessages,
} from '../constants';

import { StartDateInput } from './start-date-input';
import { DueDateInput } from './due-date-input';
import { ClassesSection } from './classes-section';
import { DateRadioOption } from './date-radio-option';
import styles from './styles.module.scss';
import { ActivitiesSection } from './activities-section/activities-section';

export const AssignmentPanel = () => {
  const { t } = useTranslation();

  const ASSIGNMENTS_ERROR_MESSAGE = getAssignmentsErrorMessages(t);

  const [isDueNow, setIsDueNow] = useState(true);
  const [isStartNow, setIsStartNow] = useState(true);
  const [isDueDateValid, setIsDueDateValid] = useState(false);
  const [isStartDateValid, setIsStartDateValid] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startDateInputDate, setStartDateInputDate] = useState<Date | null>(
    null,
  );
  const [dueDateInputDate, setDueDateInputDate] = useState<Date | null>(null);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);
  const [minDueDate, setMinDueDate] = useState<Date | null>(null);
  const [isDueDatePopoverOpen, setIsDueDatePopoverOpen] = useState(false);
  const [showDueDateValidation, setShowDueDateValidation] = useState(false);
  const [isStartDatePopoverOpen, setIsStartDatePopoverOpen] = useState(false);
  const [showStartDateValidation, setShowStartDateValidation] = useState(false);
  const [maxStartDate, setMaxStartDate] = useState<Date | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const startDateInputRef = useRef<HTMLDivElement>(null);
  const dueDateInputRef = useRef<HTMLDivElement>(null);
  const startDateRadioRef = useRef<HTMLDivElement>(null);
  const dueDateRadioRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  const { writeAssignmentActionTimestamp } =
    useAssignmentActionTimestampHandlers();

  const [editAssignment] = useEditAssignmentMutation();

  const { editingAssignment } = useAppSelector(
    (state) => state.assignmentPanel,
  );

  const editingAssignmentContent =
    editingAssignment && getAssignmentContent(editingAssignment);

  const width = useScreenWidth();
  const isBigMobileLayout = width <= RESPONSIVE_BIG_MOBILE;
  const dateInputPosition = isBigMobileLayout
    ? CalendarWidgetPositions.TOP
    : CalendarWidgetPositions.LEFT;

  // initialize date inputs with assignment data
  useEffect(() => {
    if (!editingAssignment) return;

    if (editingAssignment.startDate) {
      const initialStartDate = editingAssignment.startDate
        ? new Date(editingAssignment.startDate)
        : null;

      setStartDateInputDate(initialStartDate);
    }

    if (editingAssignment.dueDate) {
      const initialDueDate = editingAssignment.dueDate
        ? new Date(editingAssignment.dueDate)
        : null;

      setDueDateInputDate(initialDueDate);
    }
  }, [editingAssignment]);

  useCalendarsKeyboardClose(wrapperRef);

  useEffect(() => {
    // Validate dueDate field on Due-date popover close.
    if (!isDueDatePopoverOpen) {
      const isDueDateSelectionNotCompleted =
        !isDueNow && !dueDate && !editingAssignment?.dueDate;
      setShowDueDateValidation(isDueDateSelectionNotCompleted);
    }
  }, [isDueDatePopoverOpen, isDueNow, dueDate, editingAssignment?.dueDate]);

  useEffect(() => {
    // Validate startDate field on Start-date popover close.
    if (!isStartDatePopoverOpen) {
      const isStartDateSelectionNotCompleted =
        !isStartNow && !startDate && !editingAssignment?.startDate;
      setShowStartDateValidation(isStartDateSelectionNotCompleted);
    }
  }, [
    isStartDatePopoverOpen,
    isStartNow,
    startDate,
    editingAssignment?.startDate,
  ]);

  const hideAssignmentPanel = (showNotification?: boolean) => {
    dispatch(setIsAssignmentPanelOpened(false));
    dispatch(setEditingAssignment(null));

    // notification popup can be shown only after assignment panel closing
    if (showNotification) {
      dispatch(setIsAssignmentActionNotificationActive(true));
    }
  };

  const { isMounted, handleAnimatedUnmount } = useAnimatedMounting(
    hideAssignmentPanel,
    MODAL_ANIMATION_DURATION,
  );

  const { classData } = useClassData();

  const setDateInputsPositions = useCallback(() => {
    setDateInputPosition(dueDateInputRef, dueDateRadioRef);
    setDateInputPosition(startDateInputRef, startDateRadioRef);
  }, []);

  useEffect(() => {
    if (!editingAssignment) return;
    const { startDate, dueDate } = editingAssignment;
    // set start of the current day as min date
    const minDateValue = getMinDate(null);
    const maxDateValue = getMaxDate(minDateValue);
    setMinDate(minDateValue);
    setMaxDate(maxDateValue);
    setMaxStartDate(maxDateValue);

    const isDueNow =
      !dueDate || !!(dueDate && dateService.dateIsEndOfDay(dueDate));

    // if defaultDate is null minDueDate should be defined for correct work of AmplifyDateInput
    setMinDueDate(getMinDate(minDate));

    setIsDueNow(isDueNow);

    const isStartNow = !startDate;
    setIsStartNow(isStartNow);

    if (isMounted) {
      setDateInputsPositions();
    }
    //   eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAssignment, isMounted, setDateInputsPositions]);

  const handleCloseClick = () => {
    handleAnimatedUnmount();
    enableScroll();
  };

  useEscapeKey(handleCloseClick);

  const handleStartDateOptionClick = () => {
    handleStartDateOptionChange(true);
  };

  const handleDueDateOptionClick = () => {
    handleDueDateOptionChange(true);
  };

  const handleStartDateOptionChange = (isStartNow = false) => {
    setCalendarButtonsStatus({
      calendarRef: startDateInputRef,
      minDate,
      maxDate: maxStartDate,
    });

    if (isStartNow) {
      handleStartDateChange(null);
    }

    setIsStartDatePopoverOpen(!isStartNow);
    setIsDueDatePopoverOpen(false);
    setIsStartNow(isStartNow);
  };

  const selectDueDate = (dueDate: Date | null): void => {
    const isDueDatePassed =
      dueDate &&
      minDate &&
      (dueDate as unknown as number) < (minDate as unknown as number);
    const maxStartDate = isDueDatePassed || !dueDate ? maxDate : dueDate;

    setMaxStartDate(maxStartDate);

    setDueDate(dueDate);
  };

  const handleDueDateOptionChange = (isDueNow = false) => {
    setCalendarButtonsStatus({
      calendarRef: dueDateInputRef,
      minDate: minDueDate,
      maxDate,
    });

    if (isDueNow) {
      handleDueDateChange(null);
    }

    setIsDueDatePopoverOpen(!isDueNow);
    setIsStartDatePopoverOpen(false);
    setIsDueNow(isDueNow);
  };

  const handleDueDateChange = (date: Date | null) => {
    selectDueDate(date);

    if (date) {
      setDueDateInputDate(date);
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    const minDueDate = date ?? getMinDate(minDate);
    setMinDueDate(minDueDate);
    setStartDate(date);

    if (date) {
      setStartDateInputDate(date);
    }
  };

  const handleUpdateAssignmentBtnXApiEvent = (
    assignment: Assignment,
    newStartDate: number | null | undefined,
    newDueDate: number | null | undefined,
    isRightClick = false,
  ) => {
    const resultExtensions = generateUpdateAssignmentResultEventsPayload({
      assignment,
      newStartDate,
      newDueDate,
    });

    const assignmentClassBk = assignment.classBks[0];
    const assignmentClassGrade = classData?.[assignmentClassBk]?.grades ?? [];

    const contextExtensions = generateAssignmentActionsContextEventPayload({
      isRightClick,
      url: window.location.href,
      page: getPage(),
      section: getAssignmentSection(assignment),
      classBk: assignmentClassBk,
      classGrade: assignmentClassGrade,
      content:
        assignment.type === 'Cardstack'
          ? assignment.content[0]
          : assignment.content,
      lessonStatus: isScheduledAssignment(assignment)
        ? 'scheduled'
        : 'assigned',
      assignmentBusinessKey: assignment.businessKey,
    });

    eventService.send(
      XApiEventKeys.selectedUpdateAssignment,
      resultExtensions,
      contextExtensions,
    );
  };

  const handleUpdateAssignmentBtnClick = () => {
    if (!editingAssignment) return;

    const newStartDate = startDate ? startDate.getTime() : null;
    const newDueDate = dueDate ? dueDate.getTime() : null;

    handleUpdateAssignmentBtnXApiEvent(
      editingAssignment,
      newStartDate,
      newDueDate,
    );

    // Set date values OR null if isDueNow/isStartNow is true.
    const assignmentPayload = convertAssignmentIntoAssignmentPayload(
      editingAssignment,
      {
        dueDate: newDueDate,
        startDate: newStartDate,
      },
    );

    let isAssignmentsError = false;

    editAssignment(assignmentPayload)
      .unwrap()
      .then(() => {
        writeAssignmentActionTimestamp();

        RealtimeService.sendRealtimeEvent(
          editingAssignment.classBks[0],
          ASSIGNMENT_UPDATED_EVENT,
          {
            assignment: editingAssignment,
            startDate: startDate?.getTime() ?? null,
            dueDate: dueDate?.getTime() ?? null,
            isDeleted: editingAssignment.archived,
            newActivitiesCount: 0,
          },
        );

        dispatch(setAssignmentActionErrorMessage(null));
        isAssignmentsError = false;
      })
      .catch(() => {
        dispatch(
          setAssignmentActionErrorMessage(ASSIGNMENTS_ERROR_MESSAGE.EDIT),
        );
        isAssignmentsError = true;
      })
      .finally(() => {
        handleAnimatedUnmount(isAssignmentsError);
        enableScroll();
      });
  };

  const handleUpdateAssignmentBtnContextMenu = () => {
    if (!editingAssignment) return;

    const newStartDate = startDate ? startDate.getTime() : null;
    const newDueDate = dueDate ? dueDate.getTime() : null;

    handleUpdateAssignmentBtnXApiEvent(
      editingAssignment,
      newStartDate,
      newDueDate,
      true,
    );
  };

  const isUpdateBtnActive = useMemo(() => {
    // Disable Update button when user wants to delete all activities.
    let isAssignmentActivityRemoved;
    if (editingAssignment?.type === 'Cardstack') {
      if (
        editingAssignment.content.every(
          (activity) => activity.isActivityActive === false,
        )
      ) {
        return false;
      }
      isAssignmentActivityRemoved = editingAssignment.content.some(
        (activity) => activity.isActivityActive === false,
      );
    }

    const isDueDateSelectionNotCompleted = !isDueNow && !dueDate;
    const isStartDateSelectionNotCompleted = !isStartNow && !startDate;
    if (isDueDateSelectionNotCompleted || isStartDateSelectionNotCompleted) {
      return false;
    }

    const isDueDateValueValid = isDueNow ? true : isDueDateValid;
    const isStartDateValueValid = isStartNow ? true : isStartDateValid;

    const assignedDueDateValue = editingAssignment?.dueDate ?? null;
    const assignedStartDateValue = editingAssignment?.startDate ?? null;

    const assignedDueDate = assignedDueDateValue
      ? new Date(assignedDueDateValue)
      : null;
    const assignedStartDate = assignedStartDateValue
      ? new Date(assignedStartDateValue)
      : null;

    const dueDateChanged = dueDate !== assignedDueDate;
    const startDateChanged = startDate !== assignedStartDate;

    return (
      isDueDateValueValid &&
      isStartDateValueValid &&
      (dueDateChanged || startDateChanged || isAssignmentActivityRemoved)
    );
  }, [
    dueDate,
    editingAssignment?.dueDate,
    editingAssignment?.startDate,
    editingAssignment?.content,
    editingAssignment?.type,
    isDueDateValid,
    isDueNow,
    isStartDateValid,
    isStartNow,
    startDate,
  ]);

  return (
    <>
      <CSSTransition
        nodeRef={wrapperRef}
        in={isMounted}
        timeout={MODAL_ANIMATION_DURATION}
        classNames="slide-right"
        unmountOnExit
      >
        <Modal
          accessibilityAttributes={{
            'aria-labelledby': 'assign-panel-title',
          }}
          isVisible={isMounted}
          enableScrollOnUnmount
          isAssignmentPanelOpen
          excludeIntercomButton
        >
          <section
            className={clsx(
              styles.assignmentPanelWrapper,
              'amp-styles',
              'layout-context-compact',
              'condensed',
              'assignment-panel-wrapper',
            )}
            ref={wrapperRef}
          >
            {editingAssignment && (
              <StartDateInput
                startDateInputRef={startDateInputRef}
                startDate={startDateInputDate}
                onStartDateOptionChangeHandler={handleStartDateOptionChange}
                onStartDateChangeHandler={handleStartDateChange}
                setIsStartDateValid={setIsStartDateValid}
                minDate={minDate}
                maxStartDate={maxStartDate}
                isStartDatePopoverOpen={isStartDatePopoverOpen}
                showStartDateValidation={showStartDateValidation}
                calendarPosition={dateInputPosition}
              />
            )}
            {editingAssignment && (
              <DueDateInput
                dueDateInputRef={dueDateInputRef}
                dueDate={dueDateInputDate}
                onDueDateOptionChangeHandler={handleDueDateOptionChange}
                onDueDateChangeHandler={handleDueDateChange}
                setIsDueDateValid={setIsDueDateValid}
                minDueDate={minDueDate}
                maxDate={maxDate}
                isDueDatePopoverOpen={isDueDatePopoverOpen}
                showDueDateValidation={showDueDateValidation}
                calendarPosition={dateInputPosition}
              />
            )}
            <div
              className={styles.assignmentPanel}
              onScroll={setDateInputsPositions}
            >
              <header className={styles.header}>
                <h3 className={styles.title} id="assign-panel-title">
                  {t('Assignment')}
                </h3>
                <button
                  onClick={handleCloseClick}
                  className={clsx('btn', 'btn-icon-ghost', styles.closeBtn)}
                  aria-label="Close"
                  data-testid="close-panel-btn"
                >
                  <i className="icon-v2-close" aria-hidden />
                </button>
              </header>
              <div className={styles.body}>
                {editingAssignment?.type === 'Cardstack' ? (
                  <div className={styles.assignmentPanelSection}>
                    <p className={styles.sectionTitle}>{t('Activities')}</p>
                    <div className={styles.sectionContent}>
                      <ActivitiesSection
                        editingAssignment={editingAssignment}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={styles.assignmentPanelSection}>
                    <p className={styles.sectionTitle}>{t('Lessons')}</p>
                    <div className={styles.sectionContent}>
                      <p className={styles.lessonTitle}>
                        {editingAssignmentContent?.lessonTitle ??
                          editingAssignmentContent?.title}
                      </p>
                    </div>
                  </div>
                )}
                <div className={styles.assignmentPanelSection}>
                  <p className={styles.sectionTitle}>{t('Assign to')}</p>
                  <div className={styles.sectionContent}>
                    {editingAssignment && (
                      <ClassesSection
                        selectedClassesBks={editingAssignment.classBks}
                      />
                    )}
                  </div>
                </div>
                <div className={styles.assignmentPanelSection}>
                  <p className={styles.sectionTitle}>{t('Schedule')}</p>
                  <div
                    className={clsx(styles.sectionContent, styles.dateSection)}
                  >
                    <DateRadioOption
                      isChecked={isStartNow}
                      name="start-date"
                      onChange={() => handleStartDateOptionChange(true)}
                      onClick={handleStartDateOptionClick}
                      ariaLabel="Schedule immediately"
                      radioInputValue="schedule-immediately"
                      className={clsx(
                        styles.dateInputOption,
                        'assign-panel-radio-option',
                      )}
                      label={t('Immediately')}
                    />
                    <div
                      className={`amplify-date-input-option schedule-in-advance ${
                        !isStartDateValid ? 'invalid' : ''
                      }`}
                    >
                      <DateRadioOption
                        isChecked={!isStartNow}
                        name="start-date"
                        onChange={() => handleStartDateOptionChange()}
                        onClick={() => handleStartDateOptionChange()}
                        ariaLabel="Schedule in advance"
                        radioInputValue="schedule-in-advance"
                        className={clsx(
                          styles.startDateRadio,
                          'assign-panel-radio-option',
                          'assign-panel-start-date-radio',
                        )}
                        dateRadioRef={startDateRadioRef}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.assignmentPanelSection}>
                  <p className={styles.sectionTitle}>{t('Due Date')}</p>
                  <div
                    className={clsx(styles.sectionContent, styles.dateSection)}
                  >
                    <div
                      className={`panel-section list-section due-date-section condensed`}
                    >
                      <DateRadioOption
                        isChecked={isDueNow}
                        name="due-date"
                        onChange={() => handleDueDateOptionChange(true)}
                        onClick={handleDueDateOptionClick}
                        ariaLabel="No due date"
                        radioInputValue="no-due-date"
                        label={t('No due date')}
                        className={clsx(
                          styles.dateInputOption,
                          'assign-panel-radio-option',
                        )}
                      />
                      <div
                        className={`amplify-date-input-option due-date ${
                          !isDueDateValid ? 'invalid' : ''
                        }`}
                      >
                        <DateRadioOption
                          isChecked={!isDueNow}
                          name="due-date"
                          onChange={() => handleDueDateOptionChange()}
                          onClick={() => handleDueDateOptionChange()}
                          ariaLabel="Choose a calendar date"
                          radioInputValue="calendar-date"
                          className={clsx(
                            styles.dueDateRadio,
                            'assign-panel-radio-option',
                            'assign-panel-due-date-radio',
                          )}
                          dateRadioRef={dueDateRadioRef}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={clsx(
                  styles.updateAssignmentBtnSection,
                  'update-assignment-button-section',
                )}
              >
                <button
                  className={clsx(
                    'btn',
                    'btn-blue',
                    'rhythm-button',
                    'type-scale-1',
                    styles.updateAssignmentBtn,
                    {
                      [styles.disabled]: !isUpdateBtnActive,
                      disabled: !isUpdateBtnActive,
                    },
                  )}
                  onClick={handleUpdateAssignmentBtnClick}
                  onContextMenu={handleUpdateAssignmentBtnContextMenu}
                  disabled={!isUpdateBtnActive}
                >
                  {t('Update assignment')}
                </button>
              </div>
            </div>
          </section>
        </Modal>
      </CSSTransition>
    </>
  );
};
